/**
 * @module app.shared
 */

import { Aurelia, LogManager, FrameworkConfiguration, disableConnectQueue, PLATFORM } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AppRouter } from 'aurelia-router';
import { TCustomAttribute, Backend, I18N, TValueConverter } from 'aurelia-i18n';
import { InitOptions } from 'i18next';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { EssetteLogAppender } from 'app/shared/lib/logging/log-appender';
import { LogProcessor } from 'app/shared/lib/logging/log-processor';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { I18NextLogger } from 'app/shared/lib/logging/i18next-logger';
import { DateLocaleService } from 'app/shared/services/date-locale-service';
import { configureObservables } from 'app/shared/lib/extensions/typed-observables';
import { translateRouteTitle } from 'app/shared/lib/i18n';
import { FormatterRegistry } from 'app/shared/lib/formatting/formatter-registry';
import 'app/shared/lib/extensions/polyfills';
import { signalNames } from 'app/shared/lib/signals';

//import LngDetector from 'i18next-browser-languagedetector';

export class AureliaConfiguration {

    protected readonly env: EnvironmentService;
    private started: boolean = false;

    constructor() {
        this.env = new EnvironmentService();
    }

    public async configure(aurelia: Aurelia): Promise<void> {

        configureObservables();
        disableConnectQueue();

        await this.env.load(aurelia.container);

        this.updateStartupMessage(this.env.config.startupMessage);

        aurelia.use
            .standardConfiguration()
            .developmentLogging(this.env.config.logging.logLevel)
            .feature(use => this.configureLogging(use))
            .feature(use => this.configureModules(use))
            .feature(use => this.configureFormatters(use))
            .feature(use => this.configureI18N(use))
            .feature(use => this.configureRouter(use))
            .feature(use => this.configureDateLocales(use));
    }

    public start(): void {
        if(!this.started) {
            this.env.start();
            this.started = true;
        }
    }

    private updateWelcomeMessage(message: string, fullViewMessage: string): void {
        let text = message;
        // quick check for full view mode, picking appropriate welcome message
        const hash = window.location.hash;
        if(hash && (hash.indexOf('&view=full') >= 0) || (hash.indexOf('?view=full') >= 0)) {
            text = fullViewMessage;
        }
        const element = document.getElementById('welcome-message');
        if(element) {
            element.textContent = text;
        }
    }

    private updateStartupMessage(message: string): void {
        if(!this.env.config.debug) { return; }
        const element = document.getElementById('startup-message');
        if(element) {
            element.textContent = message;
        }
    }

    private configureLogging(use: FrameworkConfiguration): void {
        const logProcessor = use.container.get(LogProcessor);
        use
            .instance(LogProcessor, logProcessor)
            .transient(AppLogger);

        LogManager.clearAppenders();
        LogManager.addAppender(new EssetteLogAppender(logProcessor));
        LogManager.setLevel(LogManager.logLevel[this.env.config.logging.logLevel] || LogManager.logLevel.info);
    }

    private configureModules(use: FrameworkConfiguration): void {
        // Load enabled features
        for(var feature of this.env.config.features) {
            use.feature(PLATFORM.moduleName(`app/${feature}`));
        }

        // Load features of enabled modules
        for(var module of this.env.config.modules) {
            use.feature(PLATFORM.moduleName(`app/${module}`));  // load resources for each enabled module
        }

        // Load test feature
        // This is out-of-date, and we want to use proper unit tests now that we have them [mwj]
        // if(this.env.config.testing.enabled) {
        //     use.feature('app/test');
        // }
    }

    /***** PLUG-IN CONFIGURATION *****/
    private configureFormatters(use: FrameworkConfiguration): void {
        const registry = new FormatterRegistry(use.container);
        use.instance(FormatterRegistry, registry).postTask(() => registry.registerStandardFormatters());
    }

    private configureI18N(use: FrameworkConfiguration): void {
        use.plugin(PLATFORM.moduleName('aurelia-i18n'), (instance: I18N) => {
            // configure namespaces from modules
            const defaultNamespace = 'ui'
            const namespaces = [defaultNamespace];
            for(const module of this.env.getModules()) {
                namespaces.push(...module.getTranslationNamespaces());
            }

            // configure explicit namespaces for non-modules/integrations
            namespaces.push('elli', 'ext');


            // configure formatters and formatValue function
            const formatter = use.container.get(FormatterRegistry) as FormatterRegistry;
            const formatFunction = (value: any, format?: string, lng?: string) =>
                formatter.formatValue(value, { format: format, language: lng });

            // configure i18next logger
            const logger = use.container.get(AppLogger) as AppLogger;
            logger.source = instance;
            logger.target = 'console';

            const aliases = ['t', 'i18n'];
            // add aliases for 't' attribute
            TCustomAttribute.configureAliases(aliases);

            // register plugins
            instance.i18next
                .use(new I18NextLogger(logger))
                .use(Backend.with(use.aurelia.loader));

            if(this.env.config.i18n.useBrowserLanguageDetector) {
                //instance.i18next.use(LngDetector);
            }

            // attach signal to 't' value converter to make it automatically update when language changes
            (TValueConverter.prototype as any).signals = [signalNames.languageChanged];

            // adapt options to your needs (see http://i18next.com/docs/options/)
            // make sure to return the promise of the setup method, in order to guarantee proper loading
            const i18nextOptions: InitOptions = {
                backend: {                                  // <-- configure backend settings
                    loadPath: 'locales/{{lng}}/{{ns}}.json', // <-- XHR settings for where to get the files from
                },
                //detection: {
                //    order: ['navigator'], //prepend 'localStorage', 'cookie', for caching
                //    lookupCookie: 'i18next',
                //    lookupLocalStorage: 'i18nextLng',
                //    caches: ['localStorage', 'cookie']
                //},
                fallbackLng: this.env.config.i18n.language,
                debug: this.env.config.i18n.debug,
                returnObjects: false,
                ns: namespaces,
                defaultNS: defaultNamespace,
                //missingInterpolationHandler: function(text: string, value: any) { return 'WHAT' }, // TODO: Requires new i18next
                returnNull: true,
                returnEmptyString: true,
                compatibilityJSON: 'v3',
                interpolation: {
                    escapeValue: false, // not needed for Aurelia
                    format: formatFunction // custom formatters
                }
            };

            // include i18n plugin-specific options
            const options = {
                ...i18nextOptions,
                attributes: aliases,
                skipTranslationOnMissingKey: this.env.config.i18n.skipTranslationOnMissingKey,  // Use false during dev. Missing keys will show a warning in console
            };

            return instance.setup(options);
        });
    }

    private configureRouter(use: FrameworkConfiguration): void {
        const router = use.container.get(AppRouter) as AppRouter;
        const i18n = use.container.get(I18N) as I18N;
        const eventAggregator = use.container.get(EventAggregator) as EventAggregator;

        router.transformTitle = (title => translateRouteTitle(i18n, title));

        eventAggregator.subscribe('i18n:locale:changed', () => {
            router.updateTitle();
        });
    }

    private configureDateLocales(use: FrameworkConfiguration): void {
        use.postTask(async () => {
            const service: DateLocaleService = use.container.get(DateLocaleService);
            await service.load([...this.env.config.i18n.dateLocales]);
        });
    }

}
